import { Component, OnInit, LOCALE_ID, Inject, Input, OnDestroy } from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { AuthFactoryService } from 'src/app/auth/authFactory.service';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { User, ContractError, Currency, MiniState, AppLanguages } from 'src/app/core/models/models';
import { Manual } from 'src/app/core/models/Manual';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { URLDataIntegration } from 'src/app/integrations/models/url-data-integration.model';
import { PlatformIntegration } from 'src/app/integrations/models/platform-integration.enum';
import { LocaleService } from 'src/app/shared/services/Locale.Service';
import { registerLocaleData } from '@angular/common';
import { ReportLogService } from 'src/app/shared/services/report-log.service';
import { ExchangeOverrideState } from 'src/app/core/models/exchange-override.model';
import { PenaltyFareBasis } from 'src/app/core/models/cancellation.model';
import { ExchangeOverrideService } from 'src/app/exchange-override/services/exchange-override.service';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { Router } from '@angular/router';
import { PnrInfo } from 'src/app/core/models/itinerary.model';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { IClientSettings } from 'src/app/core/models/agency-settings.model';
import { IntegrationsService } from 'src/app/integrations/services/integrations.service';
import { environment } from 'src/environments/environment';
import {
  IEditValuesModal,
  PenaltyOverride
} from 'src/app/core/models/cancellation-overrides.model';
import {
  IBookingCredentialsData,
  IBookingCredentialsState
} from 'src/app/core/models/booking-credentials.model';
import {
  ActionTypeText,
  ENDPROCESS,
  LANGUAGES_SELECT,
  OptionType,
  PAGETYPE
} from 'src/app/core/enums/enums';
import {
  extD,
  externalDataAssign
} from 'src/app/integrations/models/external-data-integration.model';
import {
  ExchangeAvailabilityOption,
  VoluntaryExchangeQuote
} from 'src/app/core/models/voluntary-change.model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() actionActive: string;

  user$: Observable<User>;
  error$: Observable<ContractError>;
  manualInfo$: Observable<Manual>;
  manualContent$: Observable<string>;
  overridesState$: Observable<ExchangeOverrideState>;
  option$: Observable<ExchangeAvailabilityOption>;
  currencies$: Observable<Currency[]>;
  varVoluntaryExangeQuote$: Observable<VoluntaryExchangeQuote>;
  penaltiesAdditional$: Observable<PenaltyFareBasis[]>;
  pnrInfo$: Observable<PnrInfo>;

  subscriptions = new Subscription();

  isAirline: boolean;
  user: User;
  currentLanguage: string;
  actionLanguage = LANGUAGES_SELECT;
  showLanguage: boolean;
  isDashboard: boolean = false;
  showCredentialsModal = false;
  bookingCredentialsReady = false;
  bookingCredentialsData: IBookingCredentialsData;
  bookingCredentialsState: IBookingCredentialsState = null;
  clientSettings: IClientSettings;
  isCredentialsActive = false;
  showUpdateCredentialsButton = false;
  showCredentialsRequiredText = false;
  disableCredentialsForm = false;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private layoutService: LayoutService,
    private translate: TranslateService,
    private localeService: LocaleService,
    private authFactoryService: AuthFactoryService,
    private reportLog: ReportLogService,
    private overrides: ExchangeOverrideService,
    private voluntaryService: VoluntaryChangeService,
    private settingsService: SettingsService,
    private itineraryService: ItineraryService,
    private integrationsService: IntegrationsService,
    private router: Router
  ) {}

  ngOnInit() {
    this.pnrInfo$ = this.itineraryService.getPnrInfo();
    this.isDashboard = this.router.url.includes(OptionType.Dashboard);
    this.subscriptions = new Subscription();
    this.initializeLanguage();
    this.overridesState$ = this.overrides.getExchangeOverrideState();
    this.option$ = this.voluntaryService
      .getFinalState()
      .pipe(map((result: MiniState) => (result.request ? result.request.selectedOption : null)));
    this.currencies$ = this.settingsService.getCurrencies();
    this.varVoluntaryExangeQuote$ = this.voluntaryService.getVoluntaryExchangeQuote();
    this.penaltiesAdditional$ = this.voluntaryService.getPenaltiesAdditional();
    const externalData: URLDataIntegration = JSON.parse(window.localStorage.getItem(extD));
    let currentLang = window.localStorage.getItem(externalDataAssign);
    if (externalData?.plataforma === PlatformIntegration.salesforce && currentLang == null) {
      this.translate.use(AppLanguages.spanishMexican);
      window.localStorage.setItem(externalDataAssign, 'exits');
    }
    this.layoutService.dispatchManualInfo(this.locale);
    this.user$ = this.layoutService.getUser();
    this.error$ = this.layoutService.getGlobalError();
    this.manualContent$ = this.layoutService.getManualContent();
    this.manualInfo$ = this.layoutService.getManualInfo();
    this.validateIsValidCurrentProccess();
    this.callComponentSubscriptions();
  }

  callComponentSubscriptions() {
    this.subscriptions.add(
      this.settingsService.getClientSettings().subscribe((settings) => {
        this.clientSettings = settings;
        this.isAirline = settings?.isAirline;
        this.isCredentialsActive = settings?.gdsOversignEnabled;
      })
    );

    this.subscriptions.add(
      this.layoutService.getBookingCredentialsState().subscribe((state) => {
        state.isRequired ? (this.showCredentialsModal = true) : (this.showCredentialsModal = false);
      })
    );

    this.subscriptions.add(
      this.layoutService.getBookingCredentialsState().subscribe((state) => {
        this.bookingCredentialsState = state;

        if (state?.loginStatus?.isLoading || state?.loginStatus?.isLoggedIn) {
          this.disableCredentialsForm = true;
        } else {
          this.disableCredentialsForm = false;
        }
      })
    );
  }

  validateIsValidCurrentProccess() {
    this.router.url.includes(PAGETYPE.itinerary) ||
    this.router.url.includes(PAGETYPE.cancellation) ||
    this.router.url.includes(PAGETYPE.voluntaryChange) ||
    this.router.url.includes(PAGETYPE.involuntaryChange) ||
    this.router.url.includes(PAGETYPE.specialRequests) ||
    this.router.url.includes(PAGETYPE.reRoute) ||
    this.router.url.includes(PAGETYPE.newFlow)
      ? (this.showUpdateCredentialsButton = true)
      : (this.showUpdateCredentialsButton = false);
  }

  savePenalties(penalties: PenaltyOverride[]) {
    const editValuesVoluntaryModal: IEditValuesModal = {
      penaltiesOverride: penalties
    };

    this.overrides.savePenalties(editValuesVoluntaryModal);
  }

  closeModal() {
    this.layoutService.closeGenericError();
  }

  SetReportLog(action) {
    this.reportLog.FinishByUser(action);
  }

  logout() {
    switch (this.actionActive) {
      case OptionType.Cancellation:
        this.SetReportLog(ActionTypeText.Cancellation);
        break;
      case OptionType.ExchangeNoCost:
        this.SetReportLog(ActionTypeText.Reschedule);
        break;
      case OptionType.VoluntaryChange:
        this.SetReportLog(ActionTypeText.Change);
        break;
    }

    this.integrationsService.selectCurrentProcess().subscribe((isSalesForce) => {
      if (isSalesForce) {
        this.router.navigate([PAGETYPE.endProcess]);
      } else {
        this.authFactoryService.logoff();
      }
    });
  }

  ChangeValue(event) {
    let language = event.target.value;
    if (language == 'na') {
      return;
    }

    this.localeService.registerCulture(language);
    this.layoutService.saveLanguage(language);
    registerLocaleData(language);
    this.translate.use(language);
  }

  openChangePassword(value: boolean) {
    this.layoutService.dispatchOpenChangePassword(value);
  }

  prepareManualFile(): void {
    this.manualInfo$.pipe(take(1)).subscribe((res) => {
      this.layoutService.dispatchDownloadManual(res[0].id);
    });
  }

  showManual(): void {
    this.prepareManualFile();
    setTimeout(() => {
      this.manualContent$.pipe(take(1)).subscribe((res) => {
        const file = this.dataURLtoFile(res);
        const blobUrl = URL.createObjectURL(file);
        window.open(blobUrl);
      });
    }, 1000);
  }

  dataURLtoFile(dataurl: string, filename = '') {
    let arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  private initializeLanguage(): void {
    this.subscriptions.add(
      this.layoutService
        .getLanguage()
        .pipe(
          tap((language) => {
            if (!language) {
              return;
            }
            this.localeService.registerCulture(language);
            registerLocaleData(language);
            this.translate.use(language);
            this.currentLanguage = language;
          })
        )
        .subscribe()
    );
  }

  saveCredentialsData(credentialsData: IBookingCredentialsData) {
    this.bookingCredentialsData = credentialsData;
  }

  EPRLogin() {
    this.layoutService.dispatchSabreAirlineEPRLogin(this.bookingCredentialsData);
  }

  toggleBookingCredentialsModal() {
    this.layoutService.requireBookingCredentials();
  }

  validateCredentialsForm(isCredentialsReady: boolean) {
    this.bookingCredentialsReady = isCredentialsReady;
  }

  handleCredentialsAccept() {
    if (this.bookingCredentialsState.loginStatus.isLoggedIn) {
      this.layoutService.updateBookingCredentials(this.bookingCredentialsData);
    }
  }

  pressEscOnCredentialsModal() {
    this.showCredentialsRequiredText = true;
    setTimeout(() => {
      this.showCredentialsRequiredText = false;
    }, 5000);
  }

  environmentValidation() {
    return !environment.production ? true : false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
